import "./DmaMap.scss";
import React, { useState, useMemo, useEffect, useCallback } from "react";
import * as R from "ramda";

import { useSetError } from "../redux/modals";

import { Spinner } from "./Spinner";
import { Tooltip } from "./Tooltip/Tooltip";

import { MdAdd, MdRefresh, MdRemove } from "react-icons/md";
import { Button } from "./Button/Button";
import { ButtonFrameworkVariant } from "./ButtonFramework/ButtonFramework";

const MAP_WIDTH = 1100;
const MAP_HEIGHT = 550;
export const MAP_ASPECT_RATIO = MAP_WIDTH / MAP_HEIGHT;

const BORDER_COLOR = "#c0c0c0";
const BORDER_WIDTH = 1;
const BORDER_SELECTED_COLOR = "#666";
const BORDER_SELECTED_WIDTH = 4;

export type DMACode = string;
export interface GeoData {
  impressions: number;
  responses: number;
  households: number;
  name: string;
  dma: DMACode;
  index: number;
}

export type GeoDataMap = Record<DMACode, GeoData>;

export type GeoDataObject = GeoDataMap | false | undefined;

interface DMAPath {
  path: string;
  name: string;
}

export type DMAPathMap = Record<DMACode, DMAPath>;

interface TooltipData {
  x: number;
  y: number;
  dma: DMACode;
}

interface DmaMapProps {
  renderColor?: (dma: string) => string;
  width?: number;
  height?: number;
  renderTooltip?: (dma: DMACode) => React.ReactNode;
  selection?: DMACode;
  onPathLoad?: (loaded: boolean) => void;
  onClick?: (dma: string) => void;
  borderColor?: string;
  enableZoom?: boolean;
}

export const DmaMap = React.forwardRef<SVGSVGElement | null, DmaMapProps>(
  (
    {
      renderColor,
      width = MAP_WIDTH,
      height = MAP_HEIGHT,
      renderTooltip = R.identity,
      selection: externalSelection,
      onPathLoad,
      onClick,
      borderColor,
      enableZoom = false,
    },
    ref
  ) => {
    const setError = useSetError();

    const [tooltipData, setTooltipData] = useState<TooltipData>();
    const [dmaPaths, setDmaPaths] = useState<DMAPathMap>();

    const [pathsLoading, setPathsLoading] = useState(false);

    // States for zooming and panning the DMA map
    const [zoomLevel, setZoomLevel] = useState(1);
    const [panX, setPanX] = useState(0);
    const [panY, setPanY] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    useEffect(() => {
      if (!dmaPaths && !pathsLoading) {
        (async () => {
          setPathsLoading(true);

          try {
            let dmaData = await fetch("https://cdn.blisspointmedia.com/geo/DmaPaths.json");
            let dmaPaths = await dmaData.json();
            setDmaPaths(dmaPaths);
          } catch (e) {
            setError({
              message: `Failed to get dma path information: ${
                e instanceof Error ? e.message : String(e)
              }`,
              reportError: e,
            });
          }

          setPathsLoading(false);
          if (onPathLoad) {
            onPathLoad(true);
          }
        })();
      }
    }, [pathsLoading, dmaPaths, setError, onPathLoad]);

    const dmaMap = useMemo(
      () =>
        dmaPaths && renderColor
          ? R.keys(dmaPaths).map(dma => {
              let isSelected = ((tooltipData && tooltipData.dma) || externalSelection) === dma;

              let scaleAlaska = "";
              if (dma === "743") {
                scaleAlaska = " scaleAlaska"; // transoform using scale(1.2)
              }

              return (
                <path
                  className={`dmaPath${scaleAlaska}`}
                  key={dma}
                  d={(dmaPaths || {})[dma]?.path}
                  stroke={
                    isSelected ? borderColor || BORDER_SELECTED_COLOR : borderColor || BORDER_COLOR
                  }
                  strokeWidth={isSelected ? BORDER_SELECTED_WIDTH : BORDER_WIDTH}
                  fill={renderColor(dma)}
                  onMouseEnter={e =>
                    setTooltipData({
                      x: e.pageX,
                      y: e.pageY,
                      dma,
                    })
                  }
                  // eslint-disable-next-line no-undefined
                  onMouseLeave={() => setTooltipData(undefined)}
                  onClick={onClick ? () => onClick(dma) : () => {}}
                />
              );
            })
          : null,
      [dmaPaths, renderColor, tooltipData, externalSelection, borderColor, onClick]
    );

    // Handlers for zoom and pan events
    const handleWheel = useCallback(
      (e: React.WheelEvent) => {
        e.preventDefault();
        // This limits the zoom to 10% increments for each scroll event
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        // This limits the zoom level to be between 0.5x and 5x
        const newZoom = Math.max(0.5, Math.min(5, zoomLevel * delta));
        setZoomLevel(newZoom);
      },
      [zoomLevel]
    );

    const handleMouseDown = useCallback(
      (e: React.MouseEvent) => {
        setIsDragging(true);
        setDragStart({ x: e.clientX - panX, y: e.clientY - panY });
      },
      [panX, panY]
    );

    const handleMouseMove = useCallback(
      (e: React.MouseEvent) => {
        if (isDragging) {
          setPanX(e.clientX - dragStart.x);
          setPanY(e.clientY - dragStart.y);
        }
      },
      [isDragging, dragStart.x, dragStart.y]
    );

    const handleMouseUp = useCallback(() => {
      setIsDragging(false);
    }, []);

    const resetZoom = useCallback(() => {
      setZoomLevel(1);
      setPanX(0);
      setPanY(0);
    }, []);

    return (
      <div className="dmaMap" style={{ width, height }}>
        {pathsLoading || !dmaMap ? (
          <Spinner />
        ) : enableZoom ? (
          <svg
            ref={ref}
            viewBox={`0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`}
            style={{
              width: "100%",
              height: "100%",
              cursor: isDragging ? "grabbing" : "grab",
            }}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <g transform={`translate(${panX}, ${panY}) scale(${zoomLevel})`}>{dmaMap}</g>
          </svg>
        ) : (
          <svg ref={ref} viewBox={`0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`}>
            {dmaMap}
          </svg>
        )}

        {enableZoom && (
          <div className="dmaMapControls">
            <Button
              className="dmaMapControlButton"
              onClick={() => setZoomLevel(Math.min(5, zoomLevel * 1.2))}
              icon={<MdAdd />}
              variant={ButtonFrameworkVariant.ICON_ONLY}
            />
            <Button
              className="dmaMapControlButton"
              onClick={() => setZoomLevel(Math.max(0.5, zoomLevel * 0.8))}
              icon={<MdRemove />}
              variant={ButtonFrameworkVariant.ICON_ONLY}
            />
            <Button
              className="dmaMapControlButton"
              onClick={resetZoom}
              icon={<MdRefresh />}
              variant={ButtonFrameworkVariant.ICON_ONLY}
            />
          </div>
        )}

        {tooltipData && (
          <Tooltip coords={{ x: tooltipData.x, y: tooltipData.y }} horizontalShift>
            {renderTooltip(tooltipData.dma)}
          </Tooltip>
        )}
      </div>
    );
  }
);
